import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { Favorite, FileItem, Folder } from '@/types'
import apiService from '@/services/api'

interface FavoritesContextType {
  favorites: Favorite[]
  isLoading: boolean
  addFavorite: (type: 'file' | 'folder', item: FileItem | Folder) => Promise<void>
  removeFavorite: (type: 'file' | 'folder', id: number) => Promise<void>
  toggleFavorite: (type: 'file' | 'folder', item: FileItem | Folder) => Promise<void>
  isFavorite: (type: 'file' | 'folder', id: number) => boolean
  refreshFavorites: () => Promise<void>
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined)

interface FavoritesProviderProps {
  children: ReactNode
}

export function FavoritesProvider({ children }: FavoritesProviderProps) {
  const [favorites, setFavorites] = useState<Favorite[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const loadFavorites = async () => {
    try {
      setIsLoading(true)
      const favoritesData = await apiService.getFavorites()
      setFavorites(favoritesData)
    } catch (error) {
      console.error('Failed to load favorites:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const addFavorite = async (type: 'file' | 'folder', item: FileItem | Folder) => {
    try {
      const favorite = await apiService.addFavorite(type, item.id)
      setFavorites((prev) => [...prev, favorite])
    } catch (error) {
      console.error('Failed to add favorite:', error)
      throw error
    }
  }

  const removeFavorite = async (type: 'file' | 'folder', id: number) => {
    try {
      await apiService.removeFavorite(type, id)
      setFavorites((prev) => prev.filter((fav) => !(fav.type === type && fav.item.id === id)))
    } catch (error) {
      console.error('Failed to remove favorite:', error)
      throw error
    }
  }

  const toggleFavorite = async (type: 'file' | 'folder', item: FileItem | Folder) => {
    try {
      const isCurrentlyFavorite = isFavorite(type, item.id)
      if (isCurrentlyFavorite) {
        await removeFavorite(type, item.id)
      } else {
        await addFavorite(type, item)
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error)
      throw error
    }
  }

  const isFavorite = (type: 'file' | 'folder', id: number): boolean => {
    return favorites.some((fav) => fav.type === type && fav.item.id === id)
  }

  const refreshFavorites = async () => {
    await loadFavorites()
  }

  useEffect(() => {
    loadFavorites()
  }, [])

  const value: FavoritesContextType = {
    favorites,
    isLoading,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    refreshFavorites,
  }

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>
}

export function useFavorites() {
  const context = useContext(FavoritesContext)
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider')
  }
  return context
}
